import { useMemo } from "react";
import { useBoard } from "@/hooks/useBoard";
import {
  buildWaitingQueue,
  queueCount,
  queueLiveness,
  queueNote,
  queueState,
} from "@/lib/waitingOnJeff";
import kitasksBaked from "@/data/kitasksBoard.json"; // offline floor for kit-asks (ingest-kitasks.py regenerates)

// ── The one truth for "what waits on Jeff" ─────────────────────────────────
//
// The Needs Me banner ("N things wait on your word") and the WITH YOU rail on
// In Flight answer the same question. When each read its own boards they said
// different things — 166 on one screen, "Nothing waiting on you" on the other.
// This hook is the single read: five boards, one queue, and both surfaces
// render its output. The counts cannot disagree with each other.
//
// Each row carries the action that answers it (Approve, Set aside, Send to
// Kit), so the page and the rail answer with the same verbs.
//
// The strip does not use this hook — it builds the same queue from the plain
// board views it already carries (lib/waitingOnJeff.js), so no board is
// fetched twice on that page.

export function useWaitingQueue() {
  const boards = useQueueBus();

  // The kit-asks board keeps an offline floor: the last baked rows stand in
  // while the board is unreachable, so the queue never empties just because
  // the network blinked.
  const kitItems = useMemo(() => {
    const kitasks = boards.kitasks;
    if (!kitasks) return [];
    if (kitasks.live) return kitasks.items || [];
    return kitasks.items || kitasksBaked.items || [];
  }, [boards.kitasks]);

  const items = useMemo(
    () => ({
      goals: boards.goals?.items,
      projects: boards.projects?.items,
      breakage: boards.breakage?.items,
      mail: boards.mail?.items,
      kitasks: kitItems,
    }),
    [
      boards.goals?.items,
      boards.projects?.items,
      boards.breakage?.items,
      boards.mail?.items,
      kitItems,
    ],
  );

  const pure = useMemo(() => buildWaitingQueue(items), [items]);
  const liveness = queueLiveness(boards);
  const state = queueState(liveness);

  // Attach the live action to each row. The action lives on the board that
  // produced the row, so "Approve" on a project and "Set aside" on a mail
  // message are the same verbs the Needs Me deck uses.
  //
  // A row with no actionSpec — a record whose own next step is the close-out,
  // so there is nothing to approve — gets NO action at all. The surface
  // shows a link to the board instead of a button that would fire a verb
  // that does not exist.
  const rows = pure.map((row) => {
    const a = row.actionSpec;
    if (!a) {
      return { ...row, board: boards[row.linkBoard], action: null };
    }
    const board = boards[a.board];
    return {
      ...row,
      board,
      action: async () => {
        if (!board) return null;
        if (a.verb === "phaseApproveGate") {
          return board.edit("phaseApproveGate", row.slug, {
            move: a.move,
            note: a.note,
          });
        }
        return board.act(a.verb, row.slug, a.note);
      },
    };
  });

  return {
    rows,
    boards,
    liveness,
    state,
    // The one number both surfaces show. Null when it is not knowable —
    // absent stays absent.
    count: queueCount(rows, state),
    // The count is only the whole truth when every board behind it came back.
    exact: state === "exact",
    // The line both surfaces show when the count is only part of the truth.
    note: queueNote(liveness),
  };
}

// The five boards behind the queue, fetched when the host page does not hand
// them down. Written out longhand so the hook order is fixed.
function useQueueBus() {
  const goals = useBoard("goals");
  const projects = useBoard("projects");
  const breakage = useBoard("breakage");
  const mail = useBoard("mail");
  const kitasks = useBoard("kitasks");
  return { goals, projects, breakage, mail, kitasks };
}

export default useWaitingQueue;
